import { AxiosPromise } from "axios";
import CultureLabel from "components/Culture/CultureLabel";
import CulturesInterfaceDropdown from "components/dropdowns/reactselect/CulturesInterfaceDropdown";
import TimezoneDropdown from "components/dropdowns/reactselect/TimezoneDropdown";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { Section } from "components/MainSectionTemplate/types";
import FreeFieldProfileModalPicker from "components/Picker/FreefieldProfile/FreefieldProfileModalPicker";
import { FreeFieldPermissionProfilePreview } from "components/PreviewPanel/FreeFieldPermissionProfile/FreeFieldPermissionProfilePreviewPanel";
import SmallDetails from "components/SmallDetails/SmallDetails";
import React, { useState } from "react";
import { UserFirstLastName } from "./UserFirstLastName";
import moment from "moment";

interface Props {
  userToClient: server.dto.UserForInternalAdmin | server.dto.UserForExternalAdmin;
  title: string;
  environment: server.dto.Environments;
  updateFunc: (updateInstruction: server.dto.UpdateUserToClientDetail) => AxiosPromise<server.dto.ApiResponse>;
  additionalSections: Array<Section>;
  onValueChanged: (key: string, value: any) => void;
  onNameChanged: (firstName: string, lastName: string) => void;
}

interface UserDetailState {
  firstName: string;
  lastName: string;

  culture: server.dto.Culture;
  timeZone: server.dto.TimeZone;

  freeFieldProfile: server.dto.FreeFieldProfileSmall;
}

export const UserDetailSection = (props: Props) => {
  const [user, setUser] = useState<UserDetailState>({
    firstName: props.userToClient.user.firstName,
    lastName: props.userToClient.user.lastName,

    culture: serverReference.interfaceCultures.find(c => c.lcid === props.userToClient.user.culture),
    timeZone: props.userToClient.timeZone,

    freeFieldProfile: props.userToClient.freeFieldProfile,
  });

  const onValueChanged = (key: string, value: any) => {
    if (key === "name") {
      const [firstName, lastName] = value as string[];
      setUser(prevState => ({
        ...prevState,
        firstName,
        lastName
      }));
    } else if (key === "culture") {
      setUser(prevState => ({
        ...prevState,
        culture: value
      }));
    } else if (key === "timeZone") {
      setUser(prevState => ({
        ...prevState,
        timeZone: value
      }));
    } else if (key === "freeFieldPermissionProfile") {
      setUser(prevState => ({
        ...prevState,
        freeFieldProfile: value
      }));
    } else {
      props.onValueChanged(key, value);
    }
  }

  const onSuccessfulUpdate = async (key: string, value: any) => {
    if (key === "name") {
      const [firstName, lastName] = value as string[];
      props.onNameChanged(firstName, lastName);
    }
  }

  const getUdateInstruction = (u: UserDetailState): server.dto.UpdateUserToClientDetail => ({
    language: u.culture.lcid,
    name: {
      first: u.firstName,
      insertion: null,
      last: u.lastName
    },
    timeZoneId: u.timeZone.id,
    userToClientId: props.userToClient.userToClientId,
  });

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    sections={[{
      key: "user",
      type: "regular",
      title: props.title,
      fields: [{
        key: "name",
        label: RESX.GeneralLabels.Name,
        render: () => <div>{[user.firstName, user.lastName].filter(Boolean).join(" ")}</div>,
        inPageEdit: {
          type: "confirmOrCancelButton",
          fullWidth: true,
          initialValue: [user.firstName, user.lastName],
          render: ({ value, onChange, submit, cancel }) =>
            <UserFirstLastName
              focusOnMount
              firstName={value[0]}
              lastName={value[1]}
              onChange={(firstName, lastName) => onChange([firstName, lastName])}
              onEnter={submit}
              onEsc={cancel}
            />,
          submitChange: ([firstName, lastName]: string[]) => props.updateFunc(getUdateInstruction({
            ...user,
            firstName,
            lastName
          }))
        }
      }, {
        key: "lastActivity",
        label: RESX.SoUser.LastActivity,
        render: () => props.userToClient.lastActivityUtc === null ? RESX.SoUser.NeverLoggedOn : moment(props.userToClient.lastActivityUtc).fromNow(),
      }, {
        key: "email",
        label: RESX.GeneralLabels.Email,
        render: () => props.userToClient.email,
      }, {
        key: "username",
        label: RESX.SoUser.Username,
        render: () => props.userToClient.username,
      }, {
        key: "languageAndRegionLabel",
        label: RESX.PersonalSettings.LanguageAndRegion,
        hideLabel: true,
        render: () => <h3>{RESX.PersonalSettings.LanguageAndRegion}</h3>
      }, {
        key: "culture",
        label: RESX.Language.resxLanguage,
        render: () => <CultureLabel culture={user.culture} />,
        inPageEdit: {
          render: ({ value, onChange }) => <CulturesInterfaceDropdown
            value={value}
            onChange={v => onChange(v, true)}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />,
          type: "confirmOrCancelButton",
          initialValue: user.culture,
          fullWidth: true,
          submitChange: (culture: server.dto.Culture) => props.updateFunc(getUdateInstruction({
            ...user,
            culture
          }))
        }
      }, {
        key: "timeZone",
        label: RESX.DateTime.Timezone,
        render: () => <SmallDetails
          label={`(${user.timeZone.utcOffset}) ${user.timeZone.value}`}
          subLabel={user.timeZone.displayName}
        />,
        inPageEdit: {
          render: ({ value, onChange }) => <TimezoneDropdown
            value={value}
            onChange={v => onChange(v, true)}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />,
          type: "confirmOrCancelButton",
          initialValue: user.timeZone,
          fullWidth: true,
          submitChange: (timeZone: server.dto.TimeZone) => props.updateFunc(getUdateInstruction({
            ...user,
            timeZone
          }))
        }
      }, {
        key: "freeFieldPermissionProfileLabel",
        label: RESX.FreeField.FreeFieldPermissionProfile,
        hideLabel: true,
        render: () => <h3>{RESX.FreeField.FreeFieldPermissionProfile}</h3>
      }, {
        key: "freeFieldPermissionProfile",
        label: RESX.FreeField.PermissionProfile,
        isEmpty: !user.freeFieldProfile,
        render: () => <FreeFieldPermissionProfilePreview
          freeFieldPermissionProfileId={user.freeFieldProfile.freeFieldProfileId}
          environment={props.environment}
          trigger={<a>{user.freeFieldProfile.title}</a>}
        />,
        inPageEdit: {
          render: ({ onChange }) => <div className="main-section-template-fields-padding">
            <FreeFieldProfileModalPicker
              startOpen
              value={null}
              onChange={v => onChange(v, true)}
              environment={props.environment}
              validatorKey="freeFieldProfile"
              required={false}
              canCreateNew={false}
            />
          </div>,
          type: "confirmOrCancelButton",
          initialValue: user.freeFieldProfile,
          fullWidth: true,
          submitChange: (freeFieldProfile: server.dto.FreeFieldProfileSmall) => window.CoreApi.UserToClient.SetUserToClientFreeFieldProfile({
            userToClientId: props.userToClient.userToClientId,
            freeFieldProfileId: freeFieldProfile.freeFieldProfileId,
          }),
        }
      }]
    }, ...props.additionalSections]}
  />
}